import React from 'react';
import styled from 'styled-components';
import { PageWrapper } from '@components/layout/page-wrapper';
import { GlassCard } from '@components/primitives/glass-card';
import { colors } from '@config/seafoam_colors';
import { spacing, borderRadius } from '@config/spacing';
import { typography } from '@config/typography';

const LegalContainer = styled.div`
  padding: ${spacing['4xl']} ${spacing.xl};
  max-width: 720px;
  margin: 0 auto;
`;

const LegalCard = styled(GlassCard)`
  background: ${colors.white};
  padding: ${spacing['4xl']};
  gap: ${spacing.xl};

  @media (max-width: 768px) {
    padding: ${spacing['2xl']};
  }
`;

const Title = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['4xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: 0 0 ${spacing.md} 0;
  text-align: center;
`;

const Intro = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: ${colors.textSecondary};
  line-height: ${typography.lineHeight.bodyRelaxed};
  text-align: center;
  margin: 0 0 ${spacing['2xl']} 0;
`;

const DocList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const DocItem = styled.li`
  margin: 0;
`;

const DocLink = styled.a`
  display: block;
  padding: ${spacing.lg};
  background: ${colors.glassSurface};
  border: 1px solid ${colors.glassBorder};
  border-radius: ${borderRadius.lg};
  color: ${colors.text};
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.glassSurfaceHover};
    border-color: ${colors.highlight.primary};
    transform: translateX(4px);
  }
`;

const DocTitle = styled.h2`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: 0 0 ${spacing.xs} 0;
`;

const DocDescription = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  color: ${colors.textSecondary};
  line-height: ${typography.lineHeight.bodyRelaxed};
  margin: 0;
`;

/**
 * Legal Index Page
 *
 * Menu of every legal document the site exposes. Lives at /legal and is the
 * landing point app stores and external links point at when they need a
 * single "legal" surface.
 *
 * JSX is intentionally inline (no .map()) so scripts/generate-legal-static.mjs
 * can emit a static HTML version that bots and the Google OAuth verification
 * crawler can read without executing JavaScript.
 */
export const LegalIndexPage: React.FC = () => (
  <PageWrapper contained>
    <LegalContainer>
      <LegalCard>
        <Title>Legal</Title>
        <Intro>
          Policies, terms, and information about how The Home CEO handles your data.
        </Intro>
        <DocList>
          <DocItem>
            <DocLink href="/legal/privacy-policy">
              <DocTitle>Privacy Policy</DocTitle>
              <DocDescription>
                How we collect, use, and share your data, and the choices you have.
              </DocDescription>
            </DocLink>
          </DocItem>
          <DocItem>
            <DocLink href="/legal/terms-of-service">
              <DocTitle>Terms of Service</DocTitle>
              <DocDescription>
                The terms governing your use of The Home CEO.
              </DocDescription>
            </DocLink>
          </DocItem>
          <DocItem>
            <DocLink href="/legal/data-use">
              <DocTitle>Data Use</DocTitle>
              <DocDescription>
                Plain-language FAQ on how we use the data you share with us.
              </DocDescription>
            </DocLink>
          </DocItem>
          <DocItem>
            <DocLink href="/legal/delete-my-data">
              <DocTitle>Request Data Deletion</DocTitle>
              <DocDescription>
                Request deletion of specific data categories without closing your account.
              </DocDescription>
            </DocLink>
          </DocItem>
          <DocItem>
            <DocLink href="/legal/delete-data">
              <DocTitle>Delete Your Account</DocTitle>
              <DocDescription>
                Step-by-step instructions for deleting your account and all associated data.
              </DocDescription>
            </DocLink>
          </DocItem>
          <DocItem>
            <DocLink href="/legal/contact-us">
              <DocTitle>Contact Us</DocTitle>
              <DocDescription>
                Reach us with questions about the legal documents above or anything else.
              </DocDescription>
            </DocLink>
          </DocItem>
        </DocList>
      </LegalCard>
    </LegalContainer>
  </PageWrapper>
);
